import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Loader2, Pencil, Plus } from "lucide-react";
import CompanyLogo from "@/components/shared/CompanyLogo";
import { useSearchCompaniesLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { companies as allCompanies } from "@/data/dummy";
import AddCompanyDialog from "@/components/shared/AddCompanyDialog";
import { useCanMutate } from "@/lib/rbac";

interface CompanyDetails {
  id: string;
  name: string;
  domain?: string | null;
  logoUrl?: string | null;
}

interface EditableCompanyProps {
  value: CompanyDetails | null;
  onChange: (company: CompanyDetails) => void;
  placeholder?: string;
  className?: string;
  onDrillDown?: () => void;
  onEditingChange?: (editing: boolean) => void;
}

export default function EditableCompany({
  value,
  onChange,
  placeholder = "-",
  className,
  onDrillDown,
  onEditingChange,
}: EditableCompanyProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [editing, setEditingRaw] = useState(false);
  const setEditing = useCallback((v: boolean) => {
    // Read-only users can never enter edit mode.
    if (v && !canMutate) return;
    setEditingRaw(v);
    onEditingChange?.(v);
  }, [onEditingChange, canMutate]);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [companies, setCompanies] = useState<CompanyDetails[]>([]);
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Monotonic request id to guard against out-of-order search responses:
  // only the most recently issued request is allowed to apply its results.
  const searchRequestId = useRef(0);
  const workspaceId = useWorkspaceId();
  const [addCompanyInitialName, setAddCompanyInitialName] = useState("");

  const [searchCompanies, { loading }] = useSearchCompaniesLazyQuery();

  useEffect(() => {
    if (editing) {
      setTimeout(() => {
        inputRef.current?.focus();
        setShowDropdown(true); // Show dropdown immediately with "Create new" option
      }, 0);
    }
  }, [editing]);

  // Close dropdown on outside click — check both container and fixed dropdown
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setEditing(false);
        setShowDropdown(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editing]);

  const fetchCompanies = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      // Use dummy data for preview
      const filtered = allCompanies
        .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10)
        .map(c => ({
          id: c.id,
          name: c.name,
          domain: c.website?.replace(/^https?:\/\//, ''),
          logoUrl: null,
        }));
      setCompanies(filtered);
      setShowDropdown(true);
      return;
    }

    if (!search.trim()) {
      setCompanies([]);
      setShowDropdown(false);
      return;
    }

    // Tag this request so we can discard it if a newer one is issued while
    // it's still in flight (out-of-order response race).
    const requestId = ++searchRequestId.current;

    try {
      const result = await searchCompanies({
        variables: {
          workspaceId: String(workspaceId),
          search: search.trim(),
          first: 10,
        },
      });

      // A newer request superseded this one — drop the stale results.
      if (requestId !== searchRequestId.current) return;

      const accounts = result.data?.accounts?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean)
        .map(account => ({
          id: account.id,
          name: account.properties?.name || account.domain || 'Unknown',
          domain: account.domain,
          logoUrl: (account.properties as any)?.logoUrl,
        })) || [];

      setCompanies(accounts);
      setShowDropdown(true);
    } catch (error) {
      // Ignore errors from superseded requests too.
      if (requestId !== searchRequestId.current) return;
      console.error('Error searching companies:', error);
      setCompanies([]);
      setShowDropdown(false);
    }
  }, [workspaceId, searchCompanies]);

  // Debounced search
  useEffect(() => {
    if (!searchText.trim()) {
      setCompanies([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(() => {
      fetchCompanies(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, fetchCompanies]);

  const onCompanySelected = (company: CompanyDetails) => {
    onChange(company);
    setEditing(false);
    setShowDropdown(false);
    setSearchText("");
  };

  const handleCompanyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDrillDown) {
      onDrillDown();
    }
  };

  const handleAddNewCompany = () => {
    const nameToUse = searchText.trim();
    setAddCompanyInitialName(nameToUse);
    setShowDropdown(false);
    setSearchText("");
    setEditing(false);
    // Use setTimeout to open dialog AFTER the editing branch unmounts,
    // so only the single AddCompanyDialog instance (rendered outside conditional) is present
    setTimeout(() => {
      setShowAddCompanyDialog(true);
    }, 0);
  };

  const handleCompanyCreated = (newCompany: any) => {
    const companyDetails: CompanyDetails = {
      id: newCompany.id, // Now always provided by AddCompanyForm after backend creation
      name: newCompany.name,
      domain: newCompany.website?.replace(/^https?:\/\//, ''),
      logoUrl: null,
    };
    onChange(companyDetails);
    setShowAddCompanyDialog(false);
  };

  // Calculate fixed position for dropdown to escape overflow/stacking context clipping
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!editing || !showDropdown || !inputWrapperRef.current) return;
    const updatePos = () => {
      const rect = inputWrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const MIN_WIDTH = 260;
      const width = Math.max(rect.width, MIN_WIDTH);
      // Keep dropdown within viewport when we widen past the cell
      const maxLeft = window.innerWidth - width - 8;
      const left = Math.min(rect.left, Math.max(8, maxLeft));
      setDropdownPos({ top: rect.bottom + 4, left, width });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [editing, showDropdown]);

  if (editing) {
    return (
      <>
        <div className={cn("flex items-center gap-3 text-sm relative", className)} ref={containerRef}>
          <span className="shrink-0"><CompanyLogo name={value?.name || ""} domain={value?.domain} /></span>
          <div className="flex-1 min-w-0 relative" ref={inputWrapperRef}>
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setEditing(false);
                  setShowDropdown(false);
                  setSearchText("");
                }
              }}
              placeholder={t("grid.cell.searchCompanies")}
              className="w-full bg-transparent border-b border-accent outline-none text-sm py-0 px-0"
            />
            {loading && (
              <div className="absolute right-0 top-0">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              </div>
            )}
            {showDropdown && dropdownPos && (
              <div
                ref={dropdownRef}
                className="fixed z-[100] rounded-md border border-border bg-popover shadow-md py-1 max-h-48 overflow-y-auto"
                style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
              >
                {companies.map((company) => (
                  <button
                    key={company.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onCompanySelected(company);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2"
                  >
                    <CompanyLogo name={company.name} domain={company.domain} />
                    <span className="flex-1 truncate">{company.name}</span>
                  </button>
                ))}
                {/* Always show "Create new company" option */}
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddNewCompany();
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2 border-t border-border text-accent"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>{searchText.trim() ? t("grid.cell.createNewCompanyNamed", { name: searchText.trim() }) : t("grid.cell.createNewCompany")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
        {/* AddCompanyDialog rendered outside conditional below */}
      </>
    );
  }

  return (
    <>
      <div
        className={cn("flex items-center gap-3 text-sm group", className)}
        title={!canMutate ? t("common.readOnlyCannotChange") : undefined}
      >
        <span className="shrink-0"><CompanyLogo name={value?.name || ""} domain={value?.domain}/></span>
        {value && onDrillDown ? (
          <button
            onClick={handleCompanyClick}
            className="flex-1 min-w-0 text-left hover:bg-muted/50 rounded px-1 -mx-1 transition flex items-center gap-2 text-accent"
          >
            <span className="truncate">{value.name}</span>
            {canMutate && (
              <Pencil
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditing(true);
                }}
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0 pointer-events-none group-hover:pointer-events-auto"
              />
            )}
          </button>
        ) : canMutate ? (
          <button
            onClick={() => setEditing(true)}
            className="flex-1 min-w-0 text-left hover:bg-muted/50 rounded px-1 -mx-1 transition flex items-center gap-2"
          >
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value?.name || placeholder}
            </span>
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0 pointer-events-none group-hover:pointer-events-auto" />
          </button>
        ) : (
          <span className={cn("flex-1 min-w-0 truncate", !value && "text-muted-foreground")}>
            {value?.name || placeholder}
          </span>
        )}
      </div>
      <AddCompanyDialog
        open={showAddCompanyDialog}
        onOpenChange={setShowAddCompanyDialog}
        onAdd={handleCompanyCreated}
        initialName={addCompanyInitialName}
        hideOverlay
      />
    </>
  );
}
